import { useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { OutcomeButtons } from '@/components/OutcomeButtons';
import { formatDate, statusLabel } from '@/lib/format';
import type { Nomination } from '@/types/domain';
import { strings } from '@/strings';

const CLOSED = ['promoted', 'rejected', 'declined'];

/**
 * The recruiter's inbox. Sorted newest-first and filtered to open items by
 * default, because the queue's job is to be emptied.
 *
 * The filter is applied in the client rather than the query: the whole point
 * of the "all" toggle is to see the closed ones too, and two queries that
 * differ only by a `where` cost a second index for no gain at this size.
 */
export function NominationQueuePage() {
  const [showAll, setShowAll] = useState(false);
  const { rows, loading, error } = useDocs<Nomination>(
    query(collection(db, 'nominations'), orderBy('createdAt', 'desc')),
    []
  );

  const visible = showAll ? rows : rows.filter((row) => !CLOSED.includes(row.status));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-primary">{strings.queueTitle}</h1>
        <div className="flex gap-2 text-sm">
          <button type="button" onClick={() => setShowAll(false)}
            className={`rounded-md px-3 py-1 ${showAll ? 'text-muted' : 'bg-primary text-primary-foreground'}`}>
            {strings.queueOpenOnly}
          </button>
          <button type="button" onClick={() => setShowAll(true)}
            className={`rounded-md px-3 py-1 ${showAll ? 'bg-primary text-primary-foreground' : 'text-muted'}`}>
            {strings.queueAll}
          </button>
        </div>
      </div>

      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && visible.length === 0 ? <p className="text-muted">{strings.queueEmpty}</p> : null}

      <ul className="flex flex-col gap-4">
        {visible.map((row) => (
          <li key={row.id} className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-lg font-semibold text-ink">{row.candidateName}</span>
              <span className="text-sm text-muted" dir="ltr">{row.candidatePhone}</span>
              <span className="text-sm text-muted">{statusLabel(row.status)}</span>
              <span className="text-sm text-muted" dir="ltr">{formatDate(row.createdAt)}</span>
              {row.duplicateOf ? (
                <span className="rounded-md bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                  {strings.duplicateBadge}
                </span>
              ) : null}
            </div>
            <p className="text-ink">{row.why}</p>
            <p className="text-sm text-muted">{strings.colRecommender}: {row.recommenderName}</p>
            <OutcomeButtons nominationId={row.id} status={row.status} onDone={() => {}} />
          </li>
        ))}
      </ul>
    </div>
  );
}
